import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useDataGridParams } from 'hooks/useDataGrid'
import { useFetchApi } from 'hooks/useFetchApi'
import { parseAsInteger, useQueryState } from 'nuqs'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { FilterState } from 'types/data-grid'

import { buildServerGridUrl } from './grid-url'

const DEFAULT_PAGE_SIZE = 50

export interface ServerPaginatedEnvelope<T> {
  data: T[]
  total: number
  hasMore: boolean
}

export interface UseServerDataGridOptions {
  endpoint: string
  params?: Record<string, unknown>
  storageKey?: string
  pageSizeOptions?: number[]
  defaultPageSize?: number
}

export interface UseServerDataGridResult<T> {
  rows: T[]
  total: number
  hasMore: boolean
  page: number
  totalPages: number
  setPage: (page: number) => void
  pageSize: number
  setPageSize: (size: number) => void
  pageSizeOptions: number[]
  sort: ReturnType<typeof useDataGridParams>['sort']
  toggleSort: ReturnType<typeof useDataGridParams>['toggleSort']
  setSort: ReturnType<typeof useDataGridParams>['setSort']
  filters: Record<string, FilterState>
  updateFilter: ReturnType<typeof useDataGridParams>['updateFilter']
  clearFilter: ReturnType<typeof useDataGridParams>['clearFilter']
  q: string
  setQ: (value: string) => void
  loading: boolean
  refetch: () => void
}

export function useServerDataGrid<T>(options: UseServerDataGridOptions): UseServerDataGridResult<T> {
  const { endpoint, params, storageKey, pageSizeOptions = [25, 50, 100], defaultPageSize = DEFAULT_PAGE_SIZE } = options

  const { fetchApi } = useFetchApi()
  const queryClient = useQueryClient()
  const { sort, filters, page, toggleSort, setSort, updateFilter, clearFilter, setPage } = useDataGridParams(storageKey)

  const prefix = storageKey ? `${storageKey}.` : ''
  const [urlPageSize, setUrlPageSize] = useQueryState(`${prefix}ps`, parseAsInteger)
  const [localPageSize, setLocalPageSize] = useState<number>(defaultPageSize)
  const [urlQ, setUrlQ] = useQueryState(`${prefix}q`)
  const [localQ, setLocalQ] = useState<string>('')

  const pageSize = storageKey ? (urlPageSize ?? defaultPageSize) : localPageSize
  const q = storageKey ? (urlQ ?? '') : localQ

  const setPageSize = useCallback(
    (value: number) => {
      if (storageKey) {
        void setUrlPageSize(value === defaultPageSize ? null : value)
      } else {
        setLocalPageSize(value)
      }
      setPage(0)
    },
    [storageKey, setUrlPageSize, defaultPageSize, setPage],
  )

  const setQ = useCallback(
    (value: string) => {
      if (storageKey) {
        void setUrlQ(value === '' ? null : value)
      } else {
        setLocalQ(value)
      }
      setPage(0)
    },
    [storageKey, setUrlQ, setPage],
  )

  const paramsKey = JSON.stringify(params ?? {})
  const sortField = sort?.column ?? ''
  const sortOrder = sort?.direction ?? ''

  // When the page-level params change (Active toggle, Subscription dropdown, etc.),
  // the user's current page index may no longer make sense. Reset to page 0.
  const previousParamsKey = useRef(paramsKey)
  useEffect(() => {
    if (previousParamsKey.current !== paramsKey) {
      previousParamsKey.current = paramsKey
      setPage(0)
    }
  }, [paramsKey, setPage])

  // The URL is the query key: any grid-state change is a key change, and
  // `keepPreviousData` keeps the old rows rendered (loading=true) while the
  // new page loads — the old hand-rolled behavior, plus dedup + abort.
  const url = buildServerGridUrl({ endpoint, params, filters, page, pageSize, q, sortField, sortOrder })
  const query = useQuery<ServerPaginatedEnvelope<T>>({
    queryKey: ['boombox-api', url],
    queryFn: ({ signal }) => fetchApi<ServerPaginatedEnvelope<T>>(url, { signal }),
    placeholderData: keepPreviousData,
  })

  const rows = query.data?.data ?? []
  const total = query.data?.total ?? 0
  const hasMore = query.data?.hasMore ?? false
  const loading = query.isLoading || query.isPlaceholderData

  // Prefetch the NEXT page once the current one settles, so clicking Next
  // paints instantly from cache while `staleTime: 0` still fires a fresh
  // revalidation — instant paint, zero added staleness. Mirrors the
  // Customers cursor pager's prefetch-on-settle.
  const nextUrl = hasMore
    ? buildServerGridUrl({ endpoint, params, filters, page: page + 1, pageSize, q, sortField, sortOrder })
    : null
  useEffect(() => {
    if (nextUrl === null || query.isPlaceholderData || query.isFetching) {
      return
    }
    void queryClient.prefetchQuery({
      queryKey: ['boombox-api', nextUrl],
      queryFn: ({ signal }) => fetchApi<ServerPaginatedEnvelope<T>>(nextUrl, { signal }),
    })
  }, [nextUrl, query.isPlaceholderData, query.isFetching, queryClient, fetchApi])

  const { refetch: refetchQuery } = query
  const refetch = useCallback(() => {
    void refetchQuery()
  }, [refetchQuery])

  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  return {
    rows,
    total,
    hasMore,
    page,
    totalPages,
    setPage,
    pageSize,
    setPageSize,
    pageSizeOptions,
    sort,
    toggleSort,
    setSort,
    filters,
    updateFilter,
    clearFilter,
    q,
    setQ,
    loading,
    refetch,
  }
}
